import { FileText, ShieldCheck } from '@phosphor-icons/react'
import { useState } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import manifest from '@/assets/legal/manifest.json'
import { BusyLabel } from '@/components/busy-label'
import { LocaleDateField } from '@/components/locale-date-field'
import { authApi } from '../api'
import { AuthLayout } from '../components/auth-layout'
import { FormField, TextAreaField } from '../components/form-field'
import { Notice } from '../components/notice'
import { PasswordField } from '../components/password-field'
import { loginErrorKey } from '../error-keys'
import {
  maxBirthDate,
  registerSchema,
  zodResolver,
  type RegisterValues,
} from '../schemas'

const PASSWORD_PATTERN = /^(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d]).{8,}$/

/**
 * Registration (UI_PARITY §3.3). Email is lowercased before it reaches the
 * contract, the birth date enforces 18+, and terms acceptance is required.
 * Photo/city/interests are intentionally not part of the frozen flow.
 */
export function RegisterScreen() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [busy, setBusy] = useState(false)
  const [apiErrorKey, setApiErrorKey] = useState<string | null>(null)

  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<RegisterValues>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      birthDate: '',
      password: '',
      confirmPassword: '',
      bio: '',
      acceptedTerms: false,
    },
    // The Flutter form validates as the user types and keeps the primary
    // action disabled until every requirement (including the terms) is met.
    mode: 'onChange',
  })

  const passwordValue = useWatch({ control, name: 'password' })
  const confirmValue = useWatch({ control, name: 'confirmPassword' })
  const passwordValid = PASSWORD_PATTERN.test(passwordValue)
  const passwordsMatch =
    confirmValue.length > 0 && confirmValue === passwordValue

  async function submit(values: RegisterValues) {
    setBusy(true)
    setApiErrorKey(null)
    try {
      const response = await authApi.register({
        firstName: values.firstName,
        lastName: values.lastName,
        birthDate: values.birthDate,
        bio: values.bio,
        interests: [],
        email: values.email,
        password: values.password,
        confirmPassword: values.confirmPassword,
        acceptedTerms: true,
        termsVersion: manifest.terms.version,
      })
      navigate('/verify-registration', {
        replace: true,
        state: {
          email: response.email,
          emailDeliveryAvailable: response.emailDeliveryAvailable,
          expiresInSeconds: response.expiresInSeconds,
        },
      })
    } catch (error) {
      setApiErrorKey(loginErrorKey(error))
    } finally {
      setBusy(false)
    }
  }

  const fieldError = (message?: string) => (message ? t(message) : undefined)

  return (
    <AuthLayout
      title={t('screens.register.title')}
      subtitle={t('screens.register.subtitle')}
      maxWidth={560}
      backTo="/login"
    >
      <form
        className="space-y-3"
        noValidate
        onSubmit={handleSubmit((values) => void submit(values))}
      >
        <div className="grid gap-3 sm:grid-cols-2">
          <FormField
            label={t('auth.register.firstName')}
            autoComplete="given-name"
            error={fieldError(errors.firstName?.message)}
            {...register('firstName')}
          />
          <FormField
            label={t('auth.register.lastName')}
            autoComplete="family-name"
            error={fieldError(errors.lastName?.message)}
            {...register('lastName')}
          />
        </div>

        <Controller
          name="email"
          control={control}
          render={({ field }) => (
            <FormField
              label={t('auth.register.email')}
              type="email"
              inputMode="email"
              autoComplete="email"
              autoCapitalize="none"
              spellCheck={false}
              value={field.value}
              name={field.name}
              onBlur={field.onBlur}
              onChange={(event) => field.onChange(event.target.value.toLowerCase())}
              error={fieldError(errors.email?.message)}
            />
          )}
        />

        <Controller
          name="birthDate"
          control={control}
          render={({ field, fieldState }) => (
            <LocaleDateField
              label={t('auth.register.birthDate')}
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              error={fieldError(fieldState.error?.message)}
              min="1900-01-01"
              max={maxBirthDate()}
            />
          )}
        />

        <PasswordField
          label={t('auth.register.password')}
          autoComplete="new-password"
          hint={t('auth.register.passwordHelper')}
          error={fieldError(errors.password?.message)}
          valid={passwordValid}
          {...register('password')}
        />

        <PasswordField
          label={t('auth.register.confirmPassword')}
          autoComplete="new-password"
          error={fieldError(errors.confirmPassword?.message)}
          valid={passwordsMatch}
          {...register('confirmPassword')}
        />

        <Controller
          name="bio"
          control={control}
          render={({ field }) => (
            <TextAreaField
              label={t('auth.register.bio')}
              name={field.name}
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              rows={3}
              maxLength={1000}
              error={fieldError(errors.bio?.message)}
            />
          )}
        />

        <div>
          <label className="flex items-start gap-3 text-sm font-semibold text-deep">
            <input
              type="checkbox"
              className="mt-0.5 size-5 shrink-0 accent-teal"
              aria-invalid={errors.acceptedTerms ? true : undefined}
              aria-describedby={
                errors.acceptedTerms ? 'register-terms-error' : undefined
              }
              {...register('acceptedTerms')}
            />
            <span>{t('auth.register.termsAccept')}</span>
          </label>
          {errors.acceptedTerms ? (
            <p
              id="register-terms-error"
              className="mt-1.5 text-sm font-semibold text-danger"
            >
              {t(errors.acceptedTerms.message ?? 'auth.validation.termsRequired')}
            </p>
          ) : null}
        </div>

        <div className="flex flex-col items-start">
          <Link
            to="/terms"
            className="btn-ghost min-h-11 justify-start px-0"
          >
            <FileText size={18} weight="bold" aria-hidden="true" />
            {t('auth.register.termsLink')}
          </Link>
          <Link
            to="/privacy"
            className="btn-ghost min-h-11 justify-start px-0"
          >
            <ShieldCheck size={18} weight="bold" aria-hidden="true" />
            {t('auth.register.privacyLink')}
          </Link>
        </div>

        {apiErrorKey ? <Notice tone="error">{t(apiErrorKey)}</Notice> : null}

        <button
          type="submit"
          className="btn-primary w-full"
          disabled={busy || !isValid}
          aria-busy={busy}
        >
          <BusyLabel
            busy={busy}
            label={t('auth.register.submit')}
            busyLabel={t('auth.register.submitting')}
          />
        </button>
      </form>
    </AuthLayout>
  )
}
